import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { AxiosResponse } from 'axios';

import {
  ICreatePassengerRequest,
  ICreateEmployeeRequest,
  ICreatePassengerResponse,
  ICreateStudentRequest,
  IDeletePassengerResponse,
  IDeletePassengersFilters,
  IGetPassengersSimpleListResponse,
  IGetSinglePassengerResponse,
  IUpdatePassengerRequest,
  IUpdatePassengerResponse,
  IUpdatePassengerStatusResponse,
  IUpdateStudentRequest,
  IGetPassengersFilters,
  IGetPassengersResponse,
  ICreateCaregiverRequest,
  IUpdateCaregiverRequest,
  IGetPassengersSimpleListFilters,
} from 'models/http/passenger';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from '../../http';
import { IGetHistoryResponse } from 'models/http/history';
import DataUploadBaseHttp from './utils/data-upload.http';
import { HTTP } from './core.http';
import { DataUploadModels, ModelsNames } from 'utils/enums/model-names';

class PassengersHttp extends DataUploadBaseHttp implements HTTP<IDeletePassengersFilters> {
  public static DataExportRoute = `${ModelsNames.Passengers}/data-export`;
  protected readonly route: DataUploadModels = ModelsNames.Passengers;

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async getSimple(
    filters?: IGetPassengersSimpleListFilters,
    abortController?: AbortController,
  ): Promise<IGetPassengersSimpleListResponse> {
    const queryParams = prepareQPFromFilters({
      type: filters?.type,
      ...(filters?.profile ? { profile: filters.profile } : {}),
      ...(filters?.occupationLocationId ? { occupationLocationId: filters.occupationLocationId } : {}),
    });

    return serverAxios
      .get(`/${this.route}/simple?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetPassengersSimpleListResponse>) => {
        return response.data;
      });
  }

  public async get(filters?: IGetPassengersFilters, abortController?: AbortController): Promise<IGetPassengersResponse> {
    const query = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${query.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetPassengersResponse>) => response?.data);
  }

  public async getById(id: number, abortController?: AbortController): Promise<IGetSinglePassengerResponse> {
    return serverAxios
      .get(`${this.route}/single/${id}`, {
        timeout: HttpTimeoutPriority.Medium,
        signal: abortController?.signal,
      })
      .then((response: AxiosResponse<IGetSinglePassengerResponse>) => {
        if (!response?.data) {
          throw new Error('Canceled');
        }

        return response.data;
      });
  }

  public async createDefault(data: ICreatePassengerRequest): Promise<ICreatePassengerResponse> {
    return serverAxios
      .post(`/${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreatePassengerResponse>) => {
        return response.data;
      });
  }

  public async createStudent(data: ICreateStudentRequest): Promise<ICreatePassengerResponse> {
    return serverAxios
      .post(this.route, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreatePassengerResponse>) => {
        return response.data;
      });
  }

  public async createEmployee(data: ICreateEmployeeRequest): Promise<ICreatePassengerResponse> {
    return serverAxios
      .post(`${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreatePassengerResponse>) => {
        return response.data;
      });
  }

  public async createCaregiver(data: ICreateCaregiverRequest): Promise<ICreatePassengerResponse> {
    return serverAxios
      .post(`${this.route}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreatePassengerResponse>) => {
        return response.data;
      });
  }

  public async updateDefault(id: number, data: IUpdatePassengerRequest): Promise<IUpdatePassengerResponse> {
    return serverAxios
      .put(`${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdatePassengerResponse>) => {
        return response.data;
      });
  }

  public async updateStudent(id: number, data: IUpdateStudentRequest): Promise<IUpdatePassengerResponse> {
    return serverAxios
      .put(`${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdatePassengerResponse>) => {
        return response.data;
      });
  }

  public async updateEmployee(id: number, data: ICreateEmployeeRequest): Promise<IUpdatePassengerResponse> {
    return serverAxios
      .put(`${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdatePassengerResponse>) => {
        return response.data;
      });
  }

  public async updateCaregiver(id: number, data: IUpdateCaregiverRequest): Promise<IUpdatePassengerResponse> {
    return serverAxios
      .put(`${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdatePassengerResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeletePassengerResponse>;
  public async delete(filters: IDeletePassengersFilters): Promise<IDeletePassengerResponse>;
  public async delete(filters: number[] | IDeletePassengersFilters): Promise<IDeletePassengerResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeletePassengerResponse>) => {
        return response.data;
      });
  }

  public async suspend(id: string, reason: string): Promise<IUpdatePassengerStatusResponse> {
    return serverAxios
      .put(
        `/${this.route}/${id}/suspension`,
        { reason },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IUpdatePassengerStatusResponse>) => {
        return response.data;
      });
  }

  public async unsuspend(id: string): Promise<IUpdatePassengerStatusResponse> {
    return serverAxios.delete(`/${this.route}/${id}/suspension`).then((response: AxiosResponse<IUpdatePassengerStatusResponse>) => {
      return response.data;
    });
  }

  public async resetPassword(email?: string): Promise<void> {
    return serverAxios
      .post(
        this.route,
        {
          act: 'passwordsendresettoken',
          email: email || '',
          customer: 'Moovex',
          mode: 'passenger',
        },
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then(() => {
        return;
      });
  }
}

export default PassengersHttp;
